import React, { useEffect } from "react";
import { View, ScrollView } from "react-native";
import { KeyboardAwareScrollView } from "@/components/ui/KeyboardAvoidingView";
import { Controller } from "react-hook-form";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { AppText } from "@/components/ui/AppText";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/Button";
import { AppTextInput } from "@/components/ui/TextInput";
import { useForgotPasswordForm } from "@/features/auth/hooks/useForgotPasswordForm";
import { toast } from "@/lib/toast";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import { useIsWideScreen } from "@/hooks/useIsWideScreen";
import { Image } from "@/components/ui/Image";
import { PageFooter } from "@/components/ui/PageFooter";
import { Icon } from "@/components/ui/Icon";
import { Feather } from "@expo/vector-icons";

const HERO_IMAGE = require("@assets/images/login-background-image.png");

// ─── Form content (shared mobile + web) ──────────────────────────────────────

function ForgotPasswordForm() {
  const { t, isRTL } = useI18n();
  const { form, isLoading, error, handleSubmit, router } = useForgotPasswordForm();

  useEffect(() => {
    if (!error) return;

    toast.error({
      message: t(error as TranslationKeys),
    });
  }, [error, t]);

  return (
    <View className="items-start w-full">
      <Button
        className="hidden tablet:flex px-0 lg:px-0 py-2"
        label="auth.forgotPassword.goBack"
        onPress={router.back}
        variant="text"
        icon={
          <Icon
            as={Feather}
            name={isRTL ? "arrow-right" : "arrow-left"}
            size={16}
            className="text-primary"
          />
        }
      />

      <View className="w-full items-start">
        <AppText className="text-3xl text-dark-text-50 mb-2" weight="bold">
          {t("auth.forgotPassword.title")}
        </AppText>

        <AppText className="text-base text-dark-text-15 mb-8 text-start">
          {t("auth.forgotPassword.subtitle")}
        </AppText>
      </View>

      <Controller
        control={form.control}
        name="email"
        render={({ field: { value, onChange }, fieldState: { error: fieldError } }) => (
          <AppTextInput
            required
            label="auth.forgotPassword.emailLabel"
            placeholder="auth.forgotPassword.emailPlaceholder"
            value={value}
            onChangeText={onChange}
            keyboardType="email-address"
            autoCapitalize="none"
            autoComplete="email"
            error={fieldError?.message as TranslationKeys | undefined}
          />
        )}
      />

      <Button
        className="w-full mt-3"
        label="auth.forgotPassword.sendCode"
        onPress={handleSubmit}
        loading={isLoading}
        variant="primary"
      />
    </View>
  );
}

// ─── Screen ───────────────────────────────────────────────────────────────────

export default function ForgotPasswordScreen() {
  const insets = useSafeAreaInsets();
  const isWide = useIsWideScreen();

  // ── Wide-screen web layout (≥768px): split image + form ────────────────────
  if (isWide) {
    return (
      <View className="flex-1 bg-white min-h-full">
        <View className="flex-1 flex-row">
          {/* Left image panel */}
          <Image
            source={HERO_IMAGE}
            className="flex-1 bg-blue-90 max-w-xs lg:max-w-md"
            contentFit="cover"
          />

          {/* Right scrollable form */}
          <ScrollView
            className="flex-1 bg-white"
            contentContainerStyle={{ flexGrow: 1 }}
            showsVerticalScrollIndicator={false}
          >
            <View className="flex-1 items-center justify-start py-12 px-8">
              <View className="max-w-md w-full">
                <ForgotPasswordForm />
              </View>
            </View>
            <PageFooter />
          </ScrollView>
        </View>
      </View>
    );
  }

  // ── Mobile layout ────────────────────────────────────────────────────────
  return (
    <KeyboardAwareScrollView
      contentContainerStyle={{ flexGrow: 1, paddingBottom: Math.max(insets.bottom, 24) }}
      className="bg-white py-4 tablet:py-page-y"
    >
      <View className="self-center w-full tablet:max-w-3xl px-page-x">
        <ForgotPasswordForm />
      </View>
    </KeyboardAwareScrollView>
  );
}
